import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Button, Col, Form, Input, Row } from 'antd';

import Modal from '/ui/Modal';

import supportActions from '../../../actions';

import UploadFileButton from '../../../../../components/Form/UploadFileButton';

import SUPPORT from '../../../actionTypes';

import { AppDispatch, ReduxState } from '../../../../../types/app';

type FormValues = {
  Subject: string;
  Body: string;
  Attach?: File;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const SupportAskModal = ({ open, onClose }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm<FormValues>();

  const readyStatus = useSelector(
    (state: ReduxState) => state.Support.readyStatus
  );
  const err = useSelector((state: ReduxState) => state.Support.err);
  const supportLimitMin = useSelector(
    (state: ReduxState) => state.Data.config?.support?.limitMin || 10
  );

  const handleClose = () => {
    dispatch({ type: SUPPORT.SUPPORT.INVALID });
    form.resetFields();
    onClose();
  };

  const onSubmit = (values: FormValues) => {
    void dispatch(supportActions.Support.send(values)).then((ok: boolean) => {
      if (ok) {
        form.resetFields();
        onClose();
      }
    });
  };

  return (
    <Modal
      className="support-ask-modal"
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnHidden
      width={560}
      title={intl.formatMessage({ id: 'support.askQuestion' })}
    >
      {readyStatus === SUPPORT.SUPPORT.FAILURE && err && (
        <Alert
          showIcon
          closable
          type="error"
          className="support-ask-modal__alert"
          title={
            err === 'SupportLimit' ? (
              <FormattedMessage
                id="error.SupportLimit"
                values={{ value: supportLimitMin }}
              />
            ) : (
              intl.formatMessage({
                id: `error.${err}`,
                defaultMessage: err,
              })
            )
          }
        />
      )}

      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="Subject"
          label={intl.formatMessage({ id: 'support.subject' })}
          rules={[{ required: true }, { type: 'string', min: 3, max: 255 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="Body"
          label={intl.formatMessage({ id: 'support.body' })}
          rules={[{ required: true }, { type: 'string', min: 5, max: 5000 }]}
        >
          <Input.TextArea autoSize={{ minRows: 4, maxRows: 8 }} />
        </Form.Item>

        <Row gutter={[20, 20]} align="middle">
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              className="support-ask-modal__submit-btn"
              loading={readyStatus === SUPPORT.SUPPORT.REQUESTING}
            >
              <FormattedMessage id="buttons.submit" />
            </Button>
          </Col>
          <Col>
            <Form.Item name="Attach" className="support-ask-modal__attach">
              <UploadFileButton accept="image/*" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default SupportAskModal;
